/**
 * Utilidad para procesamiento asíncrono de grandes conjuntos de datos
 * Evita bloquear el hilo principal del navegador
 */

export class AsyncDataProcessor {
  /**
   * Procesa un array en chunks de forma asíncrona
   * @param {Array} data - Array de datos a procesar
   * @param {Function} processor - Función que procesa cada item
   * @param {Object} options - Opciones de configuración
   * @param {number} options.chunkSize - Tamaño de cada chunk (default: 100)
   * @param {number} options.delay - Delay entre chunks en ms (default: 0)
   * @param {Function} options.onProgress - Callback de progreso
   * @returns {Promise<Array>} - Resultados procesados
   */
  static async processInChunks(data, processor, options = {}) {
    const {
      chunkSize = 100,
      delay = 0,
      onProgress = null
    } = options;

    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    const results = [];
    const totalItems = data.length;
    const chunks = Math.ceil(totalItems / chunkSize);

    for (let i = 0; i < chunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, totalItems);
      const chunk = data.slice(start, end);
      
      // Procesar chunk
      const chunkResults = await Promise.resolve(processor(chunk, start, end));
      if (chunkResults) {
        results.push(...(Array.isArray(chunkResults) ? chunkResults : [chunkResults]));
      }
      
      // Reportar progreso
      if (onProgress) {
        const progress = Math.round((end / totalItems) * 100);
        onProgress({
          processed: end,
          total: totalItems,
          percentage: progress,
          currentChunk: i + 1,
          totalChunks: chunks
        });
      }
      
      // Yield control al navegador
      if (delay > 0 && i < chunks - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
      } else if (i < chunks - 1) {
        // Usar requestIdleCallback si está disponible
        await new Promise(resolve => {
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(resolve);
          } else {
            setTimeout(resolve, 0);
          }
        });
      }
    }

    return results;
  }

  /**
   * Procesa datos de forma asíncrona con Web Workers (si está disponible)
   * @param {Array} data - Datos a procesar
   * @param {string} workerScript - Script del worker
   * @param {Object} options - Opciones
   * @returns {Promise<any>}
   */
  static async processWithWorker(data, workerScript, options = {}) {
    if (!window.Worker) {
      console.warn('Web Workers no disponibles, usando procesamiento normal');
      return this.processInChunks(data, options.fallbackProcessor, options);
    }

    return new Promise((resolve, reject) => {
      const worker = new Worker(workerScript);
      
      worker.onmessage = (e) => {
        if (e.data.type === 'progress' && options.onProgress) {
          options.onProgress(e.data.progress);
        } else if (e.data.type === 'complete') {
          worker.terminate();
          resolve(e.data.result);
        } else if (e.data.type === 'error') {
          worker.terminate();
          reject(new Error(e.data.error));
        }
      };
      
      worker.onerror = (error) => {
        worker.terminate();
        reject(error);
      };
      
      worker.postMessage({ data, options });
    });
  }

  /**
   * Agrupa y cuenta elementos de forma eficiente
   * @param {Array} data - Array de objetos
   * @param {Function} keyExtractor - Función que extrae la clave
   * @param {Function} valueExtractor - Función que extrae el valor
   * @returns {Promise<Object>}
   */
  static async groupAndCount(data, keyExtractor, valueExtractor = () => 1) {
    const result = {};
    
    await this.processInChunks(data, (chunk) => {
      chunk.forEach(item => {
        const key = keyExtractor(item);
        if (key !== undefined && key !== null) {
          const value = valueExtractor(item);
          if (!result[key]) {
            result[key] = { count: 0, amount: 0 };
          }
          result[key].count += 1;
          result[key].amount += (typeof value === 'number' ? value : 0);
        }
      });
    }, { chunkSize: 500 });
    
    return result;
  }

  /**
   * Procesa múltiples agregaciones en paralelo
   * @param {Array} data - Datos a procesar
   * @param {Object} aggregations - Objeto con las agregaciones
   * @returns {Promise<Object>}
   */
  static async processMultipleAggregations(data, aggregations) {
    const results = {};
    const promises = [];
    
    for (const [name, config] of Object.entries(aggregations)) {
      const promise = this.groupAndCount(
        data,
        config.keyExtractor,
        config.valueExtractor
      ).then(result => {
        results[name] = result;
      });
      promises.push(promise);
    }
    
    await Promise.all(promises);
    return results;
  }
}

/**
 * Utilidad para throttling y debouncing
 */
export class PerformanceUtils {
  /**
   * Crea una versión throttled de una función
   * @param {Function} func - Función a throttlear
   * @param {number} delay - Delay en ms
   * @returns {Function}
   */
  static throttle(func, delay) {
    let timeoutId;
    let lastExecTime = 0;
    
    return function (...args) {
      const currentTime = Date.now();
      
      if (currentTime - lastExecTime > delay) {
        lastExecTime = currentTime;
        return func.apply(this, args);
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          lastExecTime = Date.now();
          func.apply(this, args);
        }, delay - (currentTime - lastExecTime));
      }
    };
  }

  /**
   * Crea una versión debounced de una función
   * @param {Function} func - Función a debouncear
   * @param {number} delay - Delay en ms
   * @returns {Function}
   */
  static debounce(func, delay) {
    let timeoutId;
    
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  }

  /**
   * Mide el rendimiento de una operación
   * @param {string} label - Etiqueta de la operación
   * @param {Function} operation - Operación a medir
   * @returns {Promise<any>}
   */
  static async measurePerformance(label, operation) {
    const startTime = performance.now();
    console.log(`⏱️ [${label}] Iniciando...`);
    
    try {
      const result = await operation();
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      console.log(`✅ [${label}] Completado en ${duration.toFixed(2)}ms`);
      
      if (duration > 1000) {
        console.warn(`⚠️ [${label}] Operación lenta detectada (${(duration / 1000).toFixed(2)}s)`);
      }
      
      return result;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      console.error(`❌ [${label}] Error después de ${duration.toFixed(2)}ms:`, error);
      throw error;
    }
  }
}

/**
 * Cache manager para optimizar cálculos repetitivos
 */
export class CacheManager {
  constructor(maxSize = 100, ttl = 300000) { // 5 minutos por defecto
    this.cache = new Map();
    this.maxSize = maxSize;
    this.ttl = ttl;
  }

  generateKey(params) {
    return JSON.stringify(params);
  }

  get(params) {
    const key = this.generateKey(params);
    const cached = this.cache.get(key);
    
    if (cached && Date.now() - cached.timestamp < this.ttl) {
      console.log('📦 Cache hit:', key);
      return cached.data;
    }
    
    if (cached) {
      this.cache.delete(key);
    }
    
    return null;
  }

  set(params, data) {
    const key = this.generateKey(params);
    
    // Limpiar cache si alcanza el tamaño máximo
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clear() {
    this.cache.clear();
  }
}